import { TranslationEnum, TranslationMap } from "@nms-ng2/app/shared/models/translation.models";
import {
    STATUS_DISABLED_COLOR,
    STATUS_CRITICAL_COLOR,
    STATUS_MAJOR_COLOR,
    STATUS_MINOR_COLOR,
    STATUS_NORMAL_COLOR,
    STATUS_UNKNOWN_COLOR
} from "@nms-ng2/app/shared/constants/colors";
import { TranspondersStepModel } from "./wizard/steps/2-transponders/transponders-step.model";
import { IntermediatesStepModel } from "./wizard/steps/3-intermediates/intermediates-step.model";
import { AmplifiersStepModel } from "./wizard/steps/5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "./wizard/steps/6-osc-management/osc-management-step.model";
import { LinksStepModel } from "./wizard/steps/4-links/links-step.model";
import { GeneralStepModel } from "./wizard/steps/7-general/general-step.model";
import { EndsStepModel } from "./wizard/steps/1-ends/ends-step.model";
import { CircuitPortIdentifierData } from "./wizard/steps/6-osc-management/osc-management-step.model";
import { PortData } from "./wizard/steps/1-ends/end-step-circuit-dwdm.model";

/** Enum relacionado ao campo 'Status' do circuito DWDM */
export enum CircuitStatus {
    DISABLED = "DISABLED",
    CRITICAL = "CRITICAL_FAULT",
    MAJOR = "MAJOR_FAULT",
    MINOR = "MINOR_FAULT",
    NORMAL = "NORMAL",
    UNKNOWN = "UNKNOWN"
}

/** Enum que representa as ações disponíveis para circuitos DWDM */
export enum CircuitActions {
    ADD = "ADD",
    EDIT = "EDIT",
    REMOVE = "REMOVE",
    DIAGNOSTIC = "DIAGNOSTIC",
    REMOVE_FROM_NMS = "REMOVE_FROM_NMS",
}

/** Mapa de traduções para ações de circuitos DWDM */
export const CIRCUIT_ACTIONS_TRANSLATION_MAP: TranslationEnum<CircuitActions> = {
    [CircuitActions.ADD]: "dwdm.circuit.actions.add",
    [CircuitActions.EDIT]: "dwdm.circuit.actions.edit",
    [CircuitActions.REMOVE]: "dwdm.circuit.actions.remove",
    [CircuitActions.DIAGNOSTIC]: "dwdm.circuit.actions.diagnostic",
    [CircuitActions.REMOVE_FROM_NMS]: "dwdm.circuit.actions.remove.from.nms",
};

/** Mapa de traduções para status de circuitos DWDM */
export const CIRCUIT_STATUS_TRANSLATION_MAP: TranslationEnum<CircuitStatus> = {
    [CircuitStatus.DISABLED]: "status.disabled",
    [CircuitStatus.CRITICAL]: "status.criticalFault",
    [CircuitStatus.MAJOR]: "status.majorFault",
    [CircuitStatus.MINOR]: "status.minorFault",
    [CircuitStatus.NORMAL]: "status.normal",
    [CircuitStatus.UNKNOWN]: "status.unknown"
};

/**
 * Define as cores dos status dos circuitos DWDM juntamente com as traduções.
 */
export const CIRCUIT_STATUS: Record<CircuitStatus, { translationKey: string; bgColor: string }> = {
    [CircuitStatus.DISABLED]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.DISABLED],
        bgColor: STATUS_DISABLED_COLOR
    },
    [CircuitStatus.CRITICAL]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.CRITICAL],
        bgColor: STATUS_CRITICAL_COLOR
    },
    [CircuitStatus.MAJOR]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.MAJOR],
        bgColor: STATUS_MAJOR_COLOR
    },
    [CircuitStatus.MINOR]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.MINOR],
        bgColor: STATUS_MINOR_COLOR
    },
    [CircuitStatus.NORMAL]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.NORMAL],
        bgColor: STATUS_NORMAL_COLOR
    },
    [CircuitStatus.UNKNOWN]: {
        translationKey: CIRCUIT_STATUS_TRANSLATION_MAP[CircuitStatus.UNKNOWN],
        bgColor: STATUS_UNKNOWN_COLOR
    }
};

/** Representa o modelo para listagem de circuitos DWDM */
export interface DwdmCircuitInfo {
    resourceId: number;
    name: string;
    aEnd: string;
    zEnd: string;
    muxes: string;
    status: CircuitStatus;
    description?: string;
}

/**
 * Enum que representa os campos para a tabela de circuitos DWDM.
 */
export enum DwdmCircuitsFields {
    name = "name",
    aEnd = "aEnd",
    zEnd = "zEnd",
    muxes = "muxes",
    status = "status"
}

/** Interface para tipagem das traduções do componente */
export interface DwdmCircuitTranslations {
    filter: TranslationMap;
    table: TranslationMap;
    actions: TranslationMap;
    status: TranslationMap;
    columnsSettings: TranslationMap;
}

/**
 * Interface que representa os dados de diagnóstico de uma porta.
 */
export interface PortDiagnosticData {
    circuitPortIdentifierData: CircuitPortIdentifierData;
    transceiverDiagnosticData: TransceiverDiagnosticData;
    alarmsDiagnosticData: Array<AlarmDiagnosticData>;
    description: string;
    linkStatus: string;
}

/**
 * Interface que representa os dados de diagnóstico de um transceiver.
 */
export interface TransceiverDiagnosticData {
    deviceName: string;
    portIdentifier: CircuitPortIdentifierData;
    identifierName: string;
    temperature: string;
    voltage: string;
    current: string;
    txPower: string;
    rxPower: string;
}

/**
 * Interface que representa os dados de diagnóstico de um transceiver four-hundred.
 */
export interface DwdmTransceiverDiagnosticData extends TransceiverDiagnosticData {
    osnr: string;
    prefecBer: string;
    ferc: string;
    qValue: string;
    qFactor: string;
}

/**
 * Interface que representa os dados de diagnóstico de uma porta DWDM.
 */
export interface DwdmPortDiagnosticData extends PortData {
    circuitPortIdentifierData: CircuitPortIdentifierData;
    dwdmTransceiverDiagnosticData: DwdmTransceiverDiagnosticData;
    alarmsDiagnosticData: Array<AlarmDiagnosticData>;
    description: string;
    deviceName: string;
}

/**
 * Interface que representa os dados de diagnóstico de um amplificador.
 */
export interface AmplifierDiagnosticData {
    deviceName: string;
    portIdentifier: CircuitPortIdentifierData;
    edfaId: string;
    description: string;
    inputPower: string;
    outputPower: string;
    signalPower: string;
    gain: string;
    amplifier: string;
    ldCurrent: string;
    ldEolCurrent: string;
    tecCurrent: string;
    tecVoltage: string;
    supplyVoltage: string;
    ldTemperature: string;
    caseTemperature: string;
    alarms: Array<AlarmDiagnosticData>;
}

/**
 * Interface que representa os dados de diagnóstico de um link.
 */
export interface DwdmLinkDiagnosticData {
    id: number;
    name: string;
    aEnd: string;
    zEnd: string;
    description: string;
    status: string;
    situation: string;
    distance: string;
    attenuation: string;
    weight: string;
    totalAttenuation: string;
}

/** Interface que representa os valores da tabela de detalhes do alarme */
export interface AlarmDiagnosticData {
    portIdentifier: CircuitPortIdentifierData;
    deviceName: string;
    sourceType: string;
    sourceValue: string;
    name: string;
    time: any;
    information: string;
    unstable: boolean;
    severity: AlarmSeverity;
}

/** Enum que representa os níveis de severidade dos alarmes */
export enum AlarmSeverity {
    MINOR,
    MAJOR,
    CRITICAL
}

/** Enum que representa os status dos links */
export enum LinkStatus {
    UP,
    DOWN
}

/** Mapa de traduções para status de links */
export const LINKS_STATUS_TRANSLATION_MAP: TranslationEnum<LinkStatus> = {
    [LinkStatus.UP]: "dwdm.circuit.status.link.up",
    [LinkStatus.DOWN]: "dwdm.circuit.status.link.down"
};

/** Classe utilizada para representar os dados de diagnóstico do Circuito. */
export class DwdmCircuitDiagnosticData {
    circuitId: number;
    circuitName: string;
    circuitDescription: string;
    circuitStatus: CircuitStatus;
    hundredTransceivers: Array<PortDiagnosticData>;
    dwdmTransceivers: Array<DwdmPortDiagnosticData>;
    amplifiers: Array<AmplifierDiagnosticData>;
    oscsManagement: Array<PortDiagnosticData>;
    links: Array<DwdmLinkDiagnosticData>;
    allAlarms: Array<AlarmDiagnosticData>;
    circuit: DwdmCircuitViewData;

    constructor() {
        this.circuitId = 0;
        this.circuitName = "";
        this.circuitDescription = "";
        this.circuitStatus = CircuitStatus.UNKNOWN;
    }
}
/**
 * Classe utilizada na edição dos circuitos DWDM
 */
export class DwdmCircuitViewData {
    circuitId: number;
    endsStepModel: EndsStepModel;
    transpondersStepModel: TranspondersStepModel;
    intermediatesStepModel: IntermediatesStepModel;
    amplifiersStepModel: AmplifiersStepModel;
    oscManagementStepModel: OscManagementStepModel;
    linksStepModel: LinksStepModel;
    generalStepModel: GeneralStepModel;
}

/**
 * Interface genérica de alterações para serem aplicadas no circuito.
 * @typeparam ChangesType - Tipo Union com todos tipos possíveis de alteração.
 */
export interface CircuitChangeToBeApplied<ChangesType extends string> {
    change: ChangesType;
    values?: Object[];
}

/**
 * Tipo genérico que faz um mapeamento de cada alteração com seu respectivo callback.
 * @typeparam ChangesType - Tipo Union com todos tipos possíveis de alteração.
 */
export type CircuitChangesCallbacks<ChangesType extends string> = {
    [key in ChangesType]: (...args: any[]) => void | Promise<void>;
};
